import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import useResizeObserver from 'use-resize-observer'
import {Badge} from '@components/common/base/badge'
import {Icon} from '@components/common/base/icon'
import {TwPanelTab, TwTab, TwTitle, TwTitleWrapper} from './containers'
import {TabProps} from './types'

const useShouldFade = (
  ref: React.RefObject<HTMLElement>,
  fadeable: boolean
): boolean => {
  const [fade, setFade] = useState(false)
  const isMounted = useRef(true)

  useEffect(
    () => () => {
      isMounted.current = false
    },
    []
  )

  useResizeObserver({
    ref: fadeable ? ref : null,
    onResize() {
      if (!isMounted.current) {
        return
      }
      const fadeThreshold = 2
      const wrapperEl = ref.current
      const parentWidth = wrapperEl?.getBoundingClientRect().width ?? 0
      const childWidth = wrapperEl?.firstElementChild?.scrollWidth ?? 0
      setFade(Math.abs(childWidth - parentWidth) > fadeThreshold)
    }
  })
  return fadeable && fade
}

export const Tab = forwardRef(
  (
    {
      id,
      title,
      active,
      onClick,
      onDoubleClick,
      onMouseDown,
      className,
      hint,
      dot,
      icon,
      badgeUnread,
      badgeContent,
      onBadgeClick,
      badgeClassName,
      badgeRef,
      after,
      slim,
      tight,
      fadeable = true,
      panelTab,
      allowLongTitles,
      forcedState,
      dragClickOnHover
    }: TabProps,
    ref
  ) => {
    const click = useCallback(
      (e: React.MouseEvent) => onClick?.(id, e),
      [onClick, id]
    )
    const doubleClick = useCallback(
      (e: React.MouseEvent) => onDoubleClick?.(id, e),
      [onClick, id]
    )
    const mouseDown = useCallback(
      (e: React.MouseEvent) => onMouseDown?.(id, e),
      [onMouseDown, id]
    )

    const wrapperRef = useRef<HTMLElement>(null)
    const shouldFade = useShouldFade(wrapperRef, fadeable)

    const TabComponent = panelTab ? TwPanelTab : TwTab

    return (
      <TabComponent
        ref={ref}
        data-tab-id={id}
        $forcedState={forcedState}
        $active={active}
        $slim={slim}
        $tight={tight}
        onClick={click}
        onDoubleClick={doubleClick}
        onMouseDown={mouseDown}
        title={hint ?? (typeof title === 'string' ? title : '')}
        className={className}
        drag-click-on-hover={dragClickOnHover ? '' : undefined}
      >
        {icon && <Icon icon={icon} />}
        <TwTitleWrapper ref={wrapperRef} $dot={dot}>
          <TwTitle $allowLongTitles={allowLongTitles} $fade={shouldFade}>
            {title}
          </TwTitle>
        </TwTitleWrapper>
        {badgeContent && (
          <Badge
            ref={badgeRef}
            type="rounded"
            unread={badgeUnread}
            onClick={onBadgeClick}
            className={badgeClassName}
          >
            {badgeContent}
          </Badge>
        )}
        {after}
      </TabComponent>
    )
  }
)
